import { PUBLIC_PATH } from '../../config/routes-config';
import { ReservationChannel } from '../../enums/reservation-channel.enum';
import { ReservationSource } from '../../enums/reservation-souce.enum';
import { ReservationTypeOfStay } from '../../enums/reservation-type-of-stay.enum';
import { MarketingChannel, MessagingChannel } from '../../interfaces/attribution.interface';

export const sourceLabels: Record<string, string> = {
  [ReservationSource.BN_ADMIN]: 'Admin',
  [ReservationSource.BN_APP]: 'BN App',
  [ReservationSource.BN_WEBSITE]: 'BN Website',
  [ReservationSource.CX]: 'CX',
  [ReservationSource.OFFLINE_SALES]: 'Offline Sales',
  [ReservationSource.ACQUISITION_TEAM]: 'Acquisition Team',
  // [ReservationSource.INTERNAL_LEAD]: 'Internal Lead',
  [ReservationSource.PARTNERS]: 'Partners',
  // Labelled for where it came from, not just "Partners", so the two are
  // distinguishable wherever both can appear -- the edit modal on a reservation
  // created before the split shows this one next to the new value.
  [ReservationSource.PARTNER_WEB]: 'Partner Portal',
  [ReservationSource.CHANNEL_MANAGER]: 'Channel Manager'
};

export const channelLabels: Record<string, string> = {
  [ReservationChannel.BIRDNEST]: 'Birdnest',
  [ReservationChannel.AIRBNB]: 'Airbnb',
  [ReservationChannel.BOOKING]: 'Booking',
  [ReservationChannel.EXPEDIA]: 'Expedia',
  [ReservationChannel.VRBO]: 'Vrbo'
};

export const getReservationSourceLabel = (source?: ReservationSource | string) => {
  if (!source) return 'Unknown';
  return sourceLabels[source] || source;
};

export const getReservationChannelLabel = (channel?: ReservationChannel | string) => {
  if (!channel) return 'Birdnest';
  return channelLabels[channel] || channel;
};

export const getReservationSourceImage = (source?: ReservationSource | string) => {
  switch (source) {
    case ReservationSource.BN_ADMIN:
      return `${PUBLIC_PATH}/assets/jpeg/admin.webp`;
    case ReservationSource.BN_APP:
      return `${PUBLIC_PATH}/assets/pngs/bn-app.png`;
    case ReservationSource.BN_WEBSITE:
      return `${PUBLIC_PATH}/assets/pngs/bn-website.png`;
    case ReservationSource.CHANNEL_MANAGER:
      return `${PUBLIC_PATH}/assets/jpeg/rentals-united.jpeg`;
    case ReservationSource.OFFLINE_SALES:
      return `${PUBLIC_PATH}/assets/pngs/sales.png`;
    case ReservationSource.ACQUISITION_TEAM:
      return `${PUBLIC_PATH}/assets/pngs/acquisition-team.png`;
    // No icon of its own yet: an internal lead is a referral from inside the
    // business, and the referral art says that better than the question mark
    // the default arm would otherwise render in the list and the edit modal.
    // case ReservationSource.INTERNAL_LEAD:
    //   return `${PUBLIC_PATH}/assets/pngs/referral.png`;
    case ReservationSource.CX:
      return `${PUBLIC_PATH}/assets/jpeg/cx.jpeg`;
    case ReservationSource.PARTNERS:
    case ReservationSource.PARTNER_WEB:
      return `${PUBLIC_PATH}/assets/jpeg/partner.jpeg`;
    default:
      return `${PUBLIC_PATH}/assets/pngs/question-mark.png`;
  }
};

export const getReservationChannelImage = (channel?: ReservationChannel | string) => {
  switch (channel) {
    case ReservationChannel.AIRBNB:
      return `${PUBLIC_PATH}/assets/pngs/airbnb.png`;
    case ReservationChannel.EXPEDIA:
      return `${PUBLIC_PATH}/assets/pngs/expedia.png`;
    case ReservationChannel.BOOKING:
      return `${PUBLIC_PATH}/assets/pngs/booking.png`;
    case ReservationChannel.VRBO:
      return `${PUBLIC_PATH}/assets/pngs/vrbo.png`;
    default:
      return `${PUBLIC_PATH}/assets/svgs/bird.svg`;
  }
};

export type ReservationSourceOption = { value: ReservationSource; label: string };
export type ReservationChannelOption = { value: ReservationChannel; label: string };

// The sources an admin may assign by hand. Both the create form and the edit
// modal offer exactly this list, so the two can no longer drift: before, create
// offered a fixed list and edit offered every member of the enum, which meant a
// reservation could be edited into a source it could never have been created
// as.
//
// The three members the enum has and this list does not are the ones a
// reservation typed in by an admin cannot truthfully be. `tenant-web` and
// `tenant-app` name the self-service clients -- a guest who booked themselves,
// which reaches us through website-connect and never through this tool -- and
// `admin-web` only records which tool was used rather than who brought the
// booking, which is the question the team values answer.
export const ASSIGNABLE_RESERVATION_SOURCES = [
  ReservationSource.CX,
  ReservationSource.OFFLINE_SALES,
  ReservationSource.ACQUISITION_TEAM,
  // ReservationSource.INTERNAL_LEAD,
  ReservationSource.PARTNERS,
  ReservationSource.CHANNEL_MANAGER
] as const;

export type AssignableReservationSource = typeof ASSIGNABLE_RESERVATION_SOURCES[number];

export const assignableSourceOptions: ReservationSourceOption[] =
  ASSIGNABLE_RESERVATION_SOURCES.map((source) => ({
    value: source,
    label: sourceLabels[source]
  }));

// A broker booking reaches us one way: through the partner portal. No other
// source can be true of a stay whose segment is BROKER, and this value is what
// credits the commission, so the picker offers only this one rather than
// leaving a wrong answer one click away. Offline Sales was briefly offered here
// too and is not a broker channel -- a broker worked by that team is still a
// partner booking.
export const BROKER_RESERVATION_SOURCES = [ReservationSource.PARTNERS] as const;

const brokerSourceOptions: ReservationSourceOption[] = BROKER_RESERVATION_SOURCES.map((source) => ({
  value: source,
  label: sourceLabels[source]
}));

/**
 * The sources assignable to a reservation of a given segment. BROKER narrows to
 * the single source that can credit a broker; every other segment gets the full
 * list.
 */
export const getAssignableSourceOptions = (
  typeOfStay?: ReservationTypeOfStay
): ReservationSourceOption[] =>
  typeOfStay === ReservationTypeOfStay.BROKER ? brokerSourceOptions : assignableSourceOptions;

/**
 * Source options for editing an existing reservation: the assignable list, plus
 * whatever the reservation already carries when that is not assignable. Without
 * the second part the Select renders blank for a booking made on the website or
 * the app, and opening the modal to correct only the channel would quietly
 * rewrite where that booking came from.
 *
 * BROKER is the exception: the list stays strictly `partners` and nothing is
 * appended. A broker stay is credited to the partners team and to nothing else,
 * so offering the value it happens to hold -- `partner-web` on one created
 * before the two were split, or CX on one whose segment was set later -- would
 * only make a wrong answer selectable again. The picker reads as empty until
 * someone chooses; the stored value is not touched until they do, so nothing is
 * rewritten by opening the modal.
 */
export const getEditableSourceOptions = (
  currentSource?: ReservationSource | string,
  typeOfStay?: ReservationTypeOfStay
): ReservationSourceOption[] => {
  const options = getAssignableSourceOptions(typeOfStay);
  if (typeOfStay === ReservationTypeOfStay.BROKER) return options;
  if (!currentSource) return options;
  if (options.some((option) => option.value === currentSource)) return options;
  return [
    ...options,
    { value: currentSource as ReservationSource, label: getReservationSourceLabel(currentSource) }
  ];
};

const channelOption = (value: ReservationChannel): ReservationChannelOption => ({
  value,
  label: channelLabels[value]
});

export const DIRECT_ONLY: ReservationChannelOption[] = [channelOption(ReservationChannel.BIRDNEST)];

// `channel` is distribution only, so every source that is not the channel
// manager is a direct booking. This map previously did three jobs at once:
// marketing channels under CX (WhatsApp, Instagram), commercial agreements
// under Offline Sales (TMC, Broker) and individual brokers under Partners
// (BlueKeys, AhmedAli) -- which is why the field could never answer any of
// them. The agreement is `typeOfStay` and the marketing channel is its own
// field, both captured on the create form.
//
// Keyed on the whole enum rather than the assignable subset: a reservation that
// already carries a self-service source still has to render its channel.
//
// The edit modal can change neither `typeOfStay` nor the marketing fields yet,
// so a reservation created with the wrong attribution still has to be fixed at
// the source for now.
export const reservationChannelOptionsBySource: Record<
  ReservationSource,
  ReservationChannelOption[]
> = {
  [ReservationSource.BN_ADMIN]: DIRECT_ONLY,
  [ReservationSource.BN_APP]: DIRECT_ONLY,
  [ReservationSource.BN_WEBSITE]: DIRECT_ONLY,
  [ReservationSource.CX]: DIRECT_ONLY,
  [ReservationSource.OFFLINE_SALES]: DIRECT_ONLY,
  [ReservationSource.ACQUISITION_TEAM]: DIRECT_ONLY,
  // [ReservationSource.INTERNAL_LEAD]: DIRECT_ONLY,
  [ReservationSource.PARTNERS]: DIRECT_ONLY,
  [ReservationSource.PARTNER_WEB]: DIRECT_ONLY,
  [ReservationSource.CHANNEL_MANAGER]: [
    channelOption(ReservationChannel.BOOKING),
    channelOption(ReservationChannel.AIRBNB),
    channelOption(ReservationChannel.EXPEDIA),
    channelOption(ReservationChannel.VRBO)
  ]
};

/**
 * The marketing and messaging answers to persist for a given source. CX is the
 * only source whose team takes the call and can actually ask the guest, so
 * every other source records the absence of an answer instead.
 *
 * Shared by the create form and the source/channel modal, and the reason the
 * modal needs it is the same reason the create form does: both hide the two
 * pickers off CX, and a value picked while the source was still CX would
 * otherwise be saved against a source that could never have produced it.
 *
 * On CX the caller's own picks pass through untouched, `undefined` included --
 * JSON drops those keys, so an untouched field still never reaches the API.
 */
export const resolveAttributionChannels = (
  source: ReservationSource | undefined,
  picked: { marketingChannel?: MarketingChannel; messagingChannel?: MessagingChannel }
): { marketingChannel?: MarketingChannel; messagingChannel?: MessagingChannel } =>
  source === ReservationSource.CX
    ? picked
    : { marketingChannel: MarketingChannel.UNKNOWN, messagingChannel: MessagingChannel.NONE };
